/**
 * Trait Scoring Engine
 *
 * Given the student's answers (questionId → optionId) and the full question bank,
 * computes a 0–100 score for every trait in traitList.json.
 *
 * Algorithm:
 *  1. For each question, find the maximum value any single option contributes to each trait.
 *     Sum these maxima across all questions → maxPossible[traitId]
 *  2. For each answered question, add the chosen option's weights → raw[traitId]
 *  3. normalizedScore = round((raw / maxPossible) * 100)
 *  4. Any trait not referenced by any question defaults to 0.
 *  5. All 49 trait IDs (T01–T49) are guaranteed to be present in the output,
 *     defaulting to 0 where no question touched them.
 */

import { ALL_QUESTIONS, Question } from '../data/assessment-questions';
import traitList from '../../traitList.json';

export type TraitProfile = Record<string, number>; // traitId → 0-100

/** All valid trait IDs derived from traitList.json */
const ALL_TRAIT_IDS: string[] = (traitList as Array<{ id: string }>).map((t) => t.id);

/**
 * Compute the max possible raw score for each trait across the full question bank.
 * For each question, only the single highest-valued option per trait is counted
 * (a student can pick only one option per question).
 */
function buildMaxPossible(questions: Question[]): Record<string, number> {
  const maxPossible: Record<string, number> = {};

  for (const question of questions) {
    // Per question: highest value each trait appears with across all options
    const questionMax: Record<string, number> = {};
    for (const option of question.options) {
      for (const tw of option.traitWeights) {
        const current = questionMax[tw.traitId] ?? 0;
        if (tw.value > current) questionMax[tw.traitId] = tw.value;
      }
    }
    // Accumulate into global maxPossible
    for (const [traitId, val] of Object.entries(questionMax)) {
      maxPossible[traitId] = (maxPossible[traitId] ?? 0) + val;
    }
  }

  return maxPossible;
}

/**
 * Compute trait scores from a completed assessment.
 *
 * @param answers  Map of questionId → chosen optionId  e.g. { Q01: "B", Q02: "A", … }
 * @returns        TraitProfile with all 49 traits scored 0–100
 */
export function computeTraitProfile(
  answers: Record<string, string>
): TraitProfile {
  const maxPossible = buildMaxPossible(ALL_QUESTIONS);
  const raw: Record<string, number> = {};

  for (const question of ALL_QUESTIONS) {
    const chosenOptionId = answers[question.id];
    if (!chosenOptionId) continue;

    const option = question.options.find((o) => o.id === chosenOptionId);
    if (!option) continue;

    for (const tw of option.traitWeights) {
      raw[tw.traitId] = (raw[tw.traitId] ?? 0) + tw.value;
    }
  }

  // Normalise to 0-100 and guarantee all trait IDs are present
  const profile: TraitProfile = {};
  for (const traitId of ALL_TRAIT_IDS) {
    const r = raw[traitId] ?? 0;
    const max = maxPossible[traitId] ?? 0;
    profile[traitId] = max > 0 ? Math.round((r / max) * 100) : 0;
  }

  return profile;
}

/**
 * Return the top N traits sorted by score descending.
 */
export function getTopTraits(
  profile: TraitProfile,
  n = 5
): Array<{ traitId: string; score: number }> {
  return Object.entries(profile)
    .map(([traitId, score]) => ({ traitId, score }))
    .sort((a, b) => b.score - a.score)
    .slice(0, n);
}

/**
 * Compute average score per cluster: "STEM" | "Humanities" | "Management" | "all"
 */
export function getClusterAverages(
  profile: TraitProfile
): Record<string, number> {
  const clusters: Record<string, number[]> = {};

  for (const trait of traitList as Array<{ id: string; cluster: string }>) {
    const score = profile[trait.id] ?? 0;
    if (!clusters[trait.cluster]) clusters[trait.cluster] = [];
    clusters[trait.cluster].push(score);
  }

  const averages: Record<string, number> = {};
  for (const [cluster, scores] of Object.entries(clusters)) {
    averages[cluster] = Math.round(
      scores.reduce((sum, s) => sum + s, 0) / scores.length
    );
  }

  return averages;
}

/**
 * Return the dominant cluster (highest average, excluding "all").
 */
export function getDominantCluster(profile: TraitProfile): string {
  const averages = getClusterAverages(profile);
  const { all: _all, ...mainClusters } = averages;

  return Object.entries(mainClusters).sort((a, b) => b[1] - a[1])[0]?.[0] ?? "STEM";
}
